// Footer. The logo, link columns, office locations, social links, accreditation logos and the
// bottom line are edited in the admin (Website → Navbar, footer & offices) and come from
// useGlobalContent(); empty fields fall back to src/data/globalDefaults.js.
import React, { useEffect, useState } from "react";
import "./Footer.css";
import { socialHandles } from "../../data.js";
import { Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { localizePath } from "../../data/serviceSlugsFr";
import { useGlobalContent } from "../../components/GlobalContent";
import { E, useEd } from "../../components/Editable";

// Accreditation images
import accred1 from "../../assets/global/footer/accreditations/security-accreditation-badge-1.webp";
import accred2 from "../../assets/global/footer/accreditations/security-accreditation-badge-2.webp";
import accred3 from "../../assets/global/footer/accreditations/security-accreditation-badge-3.webp";
import bspLogo from "../../assets/global/footer/accreditations/bsp-licence-logo.webp";
import oqlfLogo from "../../assets/global/footer/accreditations/oqlf-certification-logo.webp";
import wsibLogo from "../../assets/global/footer/wsib-accreditation-logo.webp";
import ourLogo from "../../assets/global/footer/vision-securite-footer-logo.webp";

// Province images
import ontarioProvince from "../../assets/global/footer/ontario-province-service-area.webp";
import quebecProvince from "../../assets/global/footer/quebec-province-service-area.webp";

// Bundled images, by position, shown until a file is uploaded in the admin (an uploaded
// file keeps the size attributes of its position)
const BUNDLED_ACCREDITATIONS = [
  { src: accred1, width: 120, height: 120 },
  { src: accred2, width: 500, height: 268 },
  { src: accred3, width: 303, height: 90 },
  { src: bspLogo, width: 500, height: 194 },
  { src: oqlfLogo, width: 219, height: 78 },
  { src: wsibLogo, width: 160, height: 106 },
];
const BUNDLED_PROVINCES = [
  { src: quebecProvince, width: 120, height: 149 },
  { src: ontarioProvince, width: 150, height: 100 },
];

// Social icons stay in code (data.js socialHandles); the admin picks the network ("type") and link.
const NETWORKS = Object.fromEntries(socialHandles.map((s) => [s.name.toLowerCase(), s]));

const telHref = (phone) => `tel:+1${String(phone).replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "")}`;

/* Track (max-width: 900px) for mobile-only UI */
function useIsMobile(breakpoint = 900) {
  const query = `(max-width: ${breakpoint}px)`;
  // SSR/prerender-safe: deterministic initial state to avoid hydration mismatch.
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Skip during prerender so prerendered HTML matches client first render.
    if (typeof navigator !== "undefined" && navigator.userAgent.includes("HeadlessChrome")) return;
    const mql = window.matchMedia(query);
    const handler = (e) => setIsMobile(e.matches);
    if (mql.addEventListener) mql.addEventListener("change", handler);
    else mql.addListener(handler);
    setIsMobile(mql.matches);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", handler);
      else mql.removeListener(handler);
    };
  }, [query]);

  return isMobile;
}

export default function Footer() {
  const year = new Date().getFullYear();
  const isMobile = useIsMobile(900);
  const { t } = useTranslation();
  const { g, en, isFR } = useGlobalContent();
  const ed = useEd();
  const lang = isFR ? "fr" : "en";

  // Region / language badge (e.g., "CA · EN" or "CA · FR")
  const regionBadge = `${t("footer.region", { defaultValue: "CA" })} · ${lang.toUpperCase()}`;

  // Site pages are stored as English paths ("/montreal") and localized; other links as given.
  const href = (link) => (link && link.startsWith("/") ? localizePath(lang, link) : link);

  // Text in the page language; links, phone numbers, the email and images from the English side.
  const enFooter = en.footer;
  const columns = (g.footer.columns || []).map((col, i) => ({
    title: col.title,
    links: (col.links || []).map((r, j) => ({ label: r.label, link: r.link || enFooter.columns?.[i]?.links?.[j]?.link })),
  }));
  const locations = (g.footer.locations || []).map((loc, i) => {
    const enLoc = enFooter.locations?.[i] || {};
    return { ...loc, image: loc.image || enLoc.image, phones: enLoc.phones || [], link: loc.link || enLoc.link };
  });
  const legal = (g.footer.legal || []).map((l, i) => ({ label: l.label, link: l.link || enFooter.legal?.[i]?.link }));
  const accreditations = (g.accreditations.items || []).map((a, i) => ({ alt: a.alt, logo: a.logo || en.accreditations.items?.[i]?.logo }));
  const copyright = (g.footer.copyright || "").replace(/\{\{?\s*year\s*\}?\}/g, year);

  // A link-column entry (no link: plain text)
  const columnLink = (r, i, j) => {
    const base = ["footer", "columns", i, "links", j];
    const editable = { path: [...base, "label"], max: 80, ...ed.link([...base, "link"], { shared: true }) };
    return r.link ? (
      <E as={Link} className="gw__link" to={href(r.link)} {...editable}>
        {r.label}
      </E>
    ) : (
      <E as="span" className="gw__link" {...editable}>{r.label}</E>
    );
  };

  // One office (desktop column and mobile accordion)
  const renderLocation = (loc, i) => {
    const base = ["footer", "locations", i];
    const bundled = BUNDLED_PROVINCES[i];
    const src = loc.image || bundled?.src;
    return (
      <div className="gw__location-item" key={i} {...ed.item(["footer", "locations"], i, { name: "office" })}>
        {(src || ed.editing) && (
          <img
            src={src}
            alt={loc.alt}
            className="gw__location-logo"
            loading="lazy"
            width={bundled?.width}
            height={bundled?.height}
            {...ed.img([...base, "image"], { shared: true })}
          />
        )}
        <div className="gw__location-info">
          <E as="span" className="gw__location-name" path={[...base, "name"]} max={60}>
            {loc.name}
          </E>
          <E as="p" className="gw__location-address" path={[...base, "address"]} max={200}>
            {loc.address}
          </E>
          {loc.phones.map((p, j) => (
            <E
              as="a"
              key={j}
              href={telHref(p.phone)}
              className="gw__location-email"
              path={[...base, "phones", j, "phone"]}
              shared
              max={20}
              {...ed.item([...base, "phones"], j, { name: "phone" })}
            >
              {p.phone}
            </E>
          ))}
          {loc.link && (
            <a href={loc.link} className="gw__location-email" {...ed.link([...base, "link"], { shared: true })}>
              {loc.link.replace(/^mailto:/i, "")}
            </a>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <footer id="footer">
        <div className="gw">
          {/* Top bar */}
          <div className="gw__bar">
            <div className="gw__brand">
              <Link to="/" className="logo__link" aria-label="Go to homepage">
                <img src={en.footer.logo || ourLogo} alt="Vision Security" style={{ height: "8rem", objectFit: "contain" }} loading="lazy" width={239} height={214} {...ed.img(["footer", "logo"], { shared: true })} />
              </Link>
              <span className="gw__brandSec" />
            </div>

            <div className="gw__actions">
              <button
                className="gw__locale"
                aria-label={t("footer.aria.changeLocale", { defaultValue: "Change region/language" })}
                type="button"
              >
                <Globe size={16} />
                <span>{regionBadge}</span>
              </button>

              {/* Social: network + link from the admin, icons from data.js */}
              <nav className="gw__social" aria-label={t("footer.aria.social", { defaultValue: "Social media" })}>
                {(en.social || []).map((s, i) => {
                  const net = NETWORKS[s.type];
                  if (!net) return null;
                  return (
                    <a
                      key={`${net.name}-${i}`}
                      href={s.link || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={net.name}
                      title={net.name}
                      {...ed.link(["social", i, "link"], { shared: true })}
                      {...ed.item(["social"], i, { name: "social link" })}
                    >
                      {net.icon}
                    </a>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Links: desktop = static columns, mobile = accordion */}
          <div className="gw__grid">
            {columns.map((group, i) =>
              isMobile ? (
                <details className="gw__group" key={i} {...ed.item(["footer", "columns"], i, { name: "column" })}>
                  <E as="summary" className="gw__toggle" aria-controls={`gw-list-${i}`} path={["footer", "columns", i, "title"]} max={60}>
                    {group.title}
                  </E>
                  <ul className="gw__list" id={`gw-list-${i}`}>
                    {group.links.map((r, j) => (
                      <li key={j} {...ed.item(["footer", "columns", i, "links"], j, { name: "link" })}>
                        {columnLink(r, i, j)}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : (
                <div className="gw__col" key={i} {...ed.item(["footer", "columns"], i, { name: "column" })}>
                  <E as="h4" className="gw__title" path={["footer", "columns", i, "title"]} max={60}>{group.title}</E>
                  <ul className="gw__list">
                    {group.links.map((r, j) => (
                      <li key={j} {...ed.item(["footer", "columns", i, "links"], j, { name: "link" })}>
                        {columnLink(r, i, j)}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            )}

            {/* Locations Section - Added as a column in grid */}
            {!isMobile && (
              <div className="gw__col gw__col--locations">
                <E as="h4" className="gw__title" path={["footer", "locationsTitle"]} max={60}>{g.footer.locationsTitle}</E>
                <div className="gw__locations-list">
                  {locations.map(renderLocation)}
                </div>
              </div>
            )}
          </div>

          {/* Locations for Mobile - Accordion style, open by default so the address and
              phone numbers are visible without a tap (Google Ads reviews the mobile page) */}
          {isMobile && (
            <details className="gw__group" open>
              <E as="summary" className="gw__toggle" path={["footer", "locationsTitle"]} max={60}>{g.footer.locationsTitle}</E>
              <div className="gw__locations-list gw__locations-mobile">
                {locations.map(renderLocation)}
              </div>
            </details>
          )}

          {/* Accreditations Section */}
          <div className="gw__accreditations">
            <E as="h4" className="gw__accred-title" path={["accreditations", "title"]} max={80}>
              {g.accreditations.title}
            </E>
            <div className="gw__accred-grid">
              {accreditations.map((a, i) => {
                const bundled = BUNDLED_ACCREDITATIONS[i];
                const src = a.logo || bundled?.src;
                if (!src && !ed.editing) return null;
                return (
                  <img
                    key={i}
                    src={src}
                    alt={a.alt}
                    className="gw__accred-logo"
                    loading="lazy"
                    width={bundled?.width}
                    height={bundled?.height}
                    {...ed.img(["accreditations", "items", i, "logo"], { shared: true })}
                    {...ed.item(["accreditations", "items"], i, { name: "logo" })}
                  />
                );
              })}
            </div>
          </div>

          {/* Bottom bar */}
          <div className="gw__bottom">
            <p>
              {/* One text node: the prerender serialises adjacent text as a single
                  node, so "{copyright}{' · '}" made hydration fail on every page.
                  The editor shows the line with {year} so it stays automatic. */}
              {ed.editing ? (
                <>
                  <E path={["footer", "copyright"]} max={200}>{g.footer.copyright}</E>
                  {legal.length > 0 && " · "}
                </>
              ) : `${copyright}${legal.length > 0 ? " · " : ""}`}
              {legal.map((l, i) => {
                const editable = {
                  path: ["footer", "legal", i, "label"],
                  max: 80,
                  ...ed.link(["footer", "legal", i, "link"]),
                  ...ed.item(["footer", "legal"], i, { name: "link" }),
                };
                return (
                  <React.Fragment key={i}>
                    {i > 0 && " · "}
                    {l.link ? (
                      <E as={Link} to={href(l.link)} className="gw__privacy-link" {...editable}>
                        {l.label}
                      </E>
                    ) : (
                      <E as="span" className="gw__privacy-link" {...editable}>{l.label}</E>
                    )}
                  </React.Fragment>
                );
              })}
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}
